import React, { useCallback, useEffect, useState } from 'react';

import { useLocation } from 'wouter';

import { apiPost } from '../../../lib/api_v2';
import { logError } from '../../../lib/logger';
import { storeAgentChatWithGenerationTurn } from '../../../lib/widgetGenerationTurns';

import { useRichdownComposerFocusRouter } from '../../../contexts/RichdownComposerFocusRouter';
import { useRouteWorkspace } from '../../../contexts/RouteWorkspaceContext';
import { useDocumentTitleContext } from '../../../hooks/useDocumentTitleEvents';
import AgentChatContentView from '../../AgentChatView';

// Route-scoped wrapper for `/agent` and `/agent/:agentChatId`. Bare `/agent`
// bootstraps a blank chat without changing the path, while `/agent/:agentChatId`
// deep-links to a specific existing chat.
function AgentChatPageView({ agentChatId: routedAgentChatId }) {
  const [, navigate] = useLocation();
  const { setTitle } = useDocumentTitleContext();
  const { workspaceId } = useRouteWorkspace();
  const { setAgentChatComposerActive } = useRichdownComposerFocusRouter();
  const [bootstrapAgentChatId, setBootstrapAgentChatId] = useState(null);
  const agentChatId = routedAgentChatId || bootstrapAgentChatId;

  useEffect(
    function setAgentChatDocumentTitle() {
      setTitle('Search');
    },
    [setTitle],
  );

  const createAgentChat = useCallback(async () => {
    let data;
    try {
      const response = await apiPost('/agent_chats', { workspace_id: workspaceId, agent_chat: { kind: 'standard' } });
      data = await response.json();
    } catch (error) {
      logError('AgentChatPageView', 'Failed to request agent chat creation', { error });
      return { ok: false, errors: ['request_failed'] };
    }

    if (!data.ok) {
      return { ok: false, errors: data.errors };
    }

    const row = data.data.agent_chat;

    try {
      await storeAgentChatWithGenerationTurn(row);
    } catch (error) {
      logError('AgentChatPageView', 'Failed to cache agent chat in Dexie', { error });
    }

    return { ok: true, agentChatId: row.id };
  }, [workspaceId]);

  useEffect(() => {
    if (!routedAgentChatId) return;
    setBootstrapAgentChatId(null);
  }, [routedAgentChatId]);

  useEffect(() => {
    if (routedAgentChatId || bootstrapAgentChatId) return;

    let cancelled = false;

    const createInitialChat = async () => {
      try {
        const result = await createAgentChat();
        if (cancelled) return;

        if (!result.ok) {
          logError('AgentChatPageView', 'Failed to create initial agent chat', { errors: result.errors });
          return;
        }

        setBootstrapAgentChatId(result.agentChatId);
      } catch (error) {
        if (!cancelled) {
          logError('AgentChatPageView', 'Failed to create initial agent chat', { error });
        }
      }
    };

    createInitialChat();

    return () => {
      cancelled = true;
    };
  }, [bootstrapAgentChatId, createAgentChat, routedAgentChatId]);

  const handleActivateAgentChat = useCallback((newAgentChatId) => {
    window.dispatchEvent(new CustomEvent('output:activate-agent-chat', { detail: { agentChatId: newAgentChatId } }));
  }, []);

  const handleCreateAgentChat = useCallback(async () => {
    if (routedAgentChatId) {
      navigate('/agent');
      return;
    }

    const result = await createAgentChat();
    if (!result.ok) {
      alert('Failed to create agent chat');
      logError('AgentChatPageView', 'Failed to create agent chat', { errors: result.errors });
      return;
    }

    setBootstrapAgentChatId(result.agentChatId);
  }, [createAgentChat, navigate, routedAgentChatId]);

  const handleSubmitNewChatMessage = useCallback(
    (submittedAgentChatId) => {
      if (!submittedAgentChatId) return;
      if (routedAgentChatId) return;
      if (submittedAgentChatId !== bootstrapAgentChatId) return;

      navigate(`/agent/${submittedAgentChatId}`);
    },
    [bootstrapAgentChatId, navigate, routedAgentChatId],
  );

  if (!agentChatId) {
    return (
      <div
        className="flex min-h-48 flex-1 flex-col items-center justify-center gap-3 text-sm text-gray-500 dark:text-neutral-400"
        aria-busy="true"
        aria-label="Loading agent chat"
      >
        <div className="flex items-center justify-center gap-2">
          <span className="size-2 animate-bounce rounded-full bg-neutral-300 dark:bg-neutral-600" />
          <span className="size-2 animate-bounce rounded-full bg-neutral-300 [animation-delay:0.2s] dark:bg-neutral-600" />
          <span className="size-2 animate-bounce rounded-full bg-neutral-300 [animation-delay:0.4s] dark:bg-neutral-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col" onClick={setAgentChatComposerActive}>
      <AgentChatContentView
        agentChatId={agentChatId}
        onAgentChatChange={handleActivateAgentChat}
        onCreateAgentChat={handleCreateAgentChat}
        onSubmitNewChatMessage={handleSubmitNewChatMessage}
      />
    </div>
  );
}

export default AgentChatPageView;
